// The DIGEST: the sums of the inbox ledger by range (docs/design/inbox.md
// §7, R1851; round 089 cards 19 a, 20 a and 21 a).
//
// This module is pure. It turns one answer to a ledger query (§5.6) into
// the four ranges SINCE YOU LEFT, 24H, 7D and SEASON, cuts a range into
// the slots of a chart, and writes the inline SVG of the charts and the
// markup of the ledger table and of the range row. `sinceleft.ts` holds
// the view and the presses, and calls it.
//
// The ledger is on the wall clock: an hour row is one UTC wall hour and a
// day row one UTC wall day (§5.3). A range sums whole rows, so the hour or
// the day of the departure counts whole.

import { f1, toInt } from '../../util/num';
import { briefing as S } from '../../gen/strings';
import type { InboxLedgerRow, InboxLedgerState, InboxMsg } from '../../gen/proto/aquaphobia';
import { INBOX_LEDGER_HOUR_ROWS, INBOX_LEDGER_KEYS, type InboxLedgerKey } from '../../gen/inboxledger';
import { DIGEST } from '../../gen/presentation';
import { groupThousands } from '../../locale/group';
import { SEC_PER_DAY, SEC_PER_HOUR } from '../../locale/clock';
import { isoDate, isoNear, isoTime, UTC_MARK } from '../../locale/stamp';
import { dur } from '../../state';
import { esc } from '../esc';
import { tabStrip } from '../choice/choice';

/** The four ranges of the range row, in its order. */
export type RangeId = 'away' | 'h24' | 'd7' | 'season';
export const RANGE_IDS: readonly RangeId[] = ['away', 'h24', 'd7', 'season'];

const KEYS = INBOX_LEDGER_KEYS.length;
const IDX = Object.fromEntries(INBOX_LEDGER_KEYS.map((k, i) => [k, i])) as Record<InboxLedgerKey, number>;

/** One figure for each counter of the ledger, in the order of the list. */
export type Sums = number[];

/** One range of the DIGEST, summed. */
export interface Range {
  id: RangeId;
  /** The range reads hour rows or day rows. */
  unit: 'hour' | 'day';
  /** The slot of the first and of the last row: the Unix second divided
   *  by 3600 for an hour, by 86400 for a day. */
  first: number;
  last: number;
  /** The wall second of the departure for SINCE YOU LEFT, else 0. */
  away: number;
  /** The server's wall clock at the answer. */
  now: number;
  /** The sum of each counter over the range. */
  sums: Sums;
  /** The sums of the range of the same length just before it (24H, 7D),
   *  else null. */
  prev: Sums | null;
  /** One row of sums for each hour or day of the range, oldest first. */
  rows: Sums[];
}

/** One slot of a chart: its first second, and the sums of its rows. */
export interface Slot {
  start: number;
  sums: Sums;
}

/** The figure of one counter. */
export function fig(sums: Sums, k: InboxLedgerKey): number {
  return sums[IDX[k]] ?? 0;
}

function zero(): Sums {
  return new Array<number>(KEYS).fill(0);
}

function addInto(to: Sums, from: readonly number[]): void {
  const n = Math.min(KEYS, from.length);
  for (let i = 0; i < n; i++) to[i] += from[i] ?? 0;
}

/** The rows of one kind by slot. A counter past the end of a row is 0
 *  (the trailing zeros are cut, §5.6), and a counter past the client's
 *  list (a newer server) is dropped. */
function bySlot(rows: readonly InboxLedgerRow[], unit: number): Map<number, readonly number[]> {
  const out = new Map<number, readonly number[]>();
  for (const r of rows) out.set(Math.floor(Number(r.startUnix) / unit), r.counts);
  return out;
}

/** One row of sums for each slot from `first` to `last`, a slot with no
 *  row reading zeros. */
function dense(map: Map<number, readonly number[]>, first: number, last: number): Sums[] {
  const out: Sums[] = [];
  for (let s = first; s <= last; s++) {
    const row = zero();
    const c = map.get(s);
    if (c) addInto(row, c);
    out.push(row);
  }
  return out;
}

function total(rows: readonly Sums[]): Sums {
  const out = zero();
  for (const r of rows) addInto(out, r);
  return out;
}

/** The wall second of the departure, or 0 when the ledger holds none. */
export function awayOf(l: InboxLedgerState): number {
  const away = Number(l.awayUnix);
  return away > 0 && away <= Number(l.nowUnix) ? away : 0;
}

/**
 * One range of the answer, summed, or null for SINCE YOU LEFT when the
 * ledger holds no departure.
 *
 * - SINCE YOU LEFT reads the hour rows from the hour of the departure
 *   while the hour ring holds that hour, and the day rows from the UTC
 *   day of the departure after that (§5.6, F4 of the ledger review).
 * - 24H reads the present hour and the 23 before it, against the 24
 *   hours before those.
 * - 7D reads the present UTC day and the 6 before it, against the 7 days
 *   before those.
 * - SEASON reads the day rows from the first day that holds a count.
 */
export function rangeOf(l: InboxLedgerState, id: RangeId): Range | null {
  const now = Number(l.nowUnix);
  const nowH = Math.floor(now / SEC_PER_HOUR);
  const today = Math.floor(now / SEC_PER_DAY);
  const hours = bySlot(l.hours, SEC_PER_HOUR);
  const days = bySlot(l.days, SEC_PER_DAY);
  const make = (unit: 'hour' | 'day', first: number, last: number, prevFirst: number | null, away = 0): Range => {
    const map = unit === 'hour' ? hours : days;
    const rows = dense(map, first, last);
    const prev = prevFirst === null ? null : total(dense(map, prevFirst, first - 1));
    return { id, unit, first, last, away, now, sums: total(rows), prev, rows };
  };
  switch (id) {
    case 'away': {
      const away = awayOf(l);
      if (away === 0) return null;
      const ah = Math.floor(away / SEC_PER_HOUR);
      if (nowH - ah < INBOX_LEDGER_HOUR_ROWS) return make('hour', ah, nowH, null, away);
      return make('day', Math.floor(away / SEC_PER_DAY), today, null, away);
    }
    case 'h24': return make('hour', nowH - 23, nowH, nowH - 47);
    case 'd7': return make('day', today - 6, today, today - 13);
    case 'season': {
      let first = today;
      for (const s of days.keys()) first = Math.min(first, s);
      return make('day', first, today, null);
    }
  }
}

/** The first wall second a range covers: the departure itself for SINCE
 *  YOU LEFT, which a message stamp can be held to, else its first row. */
export function rangeStart(r: Range): number {
  if (r.id === 'away') return r.away;
  return r.first * (r.unit === 'hour' ? SEC_PER_HOUR : SEC_PER_DAY);
}

/** How many rows one slot of a chart of the range takes: `slot_hours`
 *  hours or one day at the least, and more when the range would draw more
 *  than `max_slots` slots. */
export function slotStep(r: Range): number {
  const n = r.last - r.first + 1;
  const min = r.unit === 'hour' ? DIGEST.slotHours : 1;
  return Math.max(min, Math.ceil(n / DIGEST.maxSlots));
}

/** The slots of a chart of the range, oldest first. The last slot can be
 *  short: it ends at the present row. */
export function slotsOf(r: Range): Slot[] {
  const step = slotStep(r);
  const unit = r.unit === 'hour' ? SEC_PER_HOUR : SEC_PER_DAY;
  const out: Slot[] = [];
  for (let i = 0; i < r.rows.length; i += step) {
    out.push({ start: (r.first + i) * unit, sums: total(r.rows.slice(i, i + step)) });
  }
  return out;
}

/** The events of a range: the six event counters (§5.1). An event is not a
 *  row of the INBOX, because a fold holds many events (F5). */
export function eventsOf(sums: Sums): number {
  return fig(sums, 'msg_loss') + fig(sums, 'msg_war') + fig(sums, 'msg_hold')
    + fig(sums, 'msg_dive') + fig(sums, 'msg_trade') + fig(sums, 'msg_world');
}

/** The works of a range: the jobs that the WORKS row counts. */
export function worksOf(sums: Sums): number {
  return fig(sums, 'jobs_done') + fig(sums, 'research_done') + fig(sums, 'ships_ready')
    + fig(sums, 'repairs') + fig(sums, 'torps_stowed') + fig(sums, 'captains_recruited');
}

/** A UTC day, `YYYY-MM-DD`, with no zone mark. */
function utcDay(sec: number): string {
  return isoDate(sec, { utc: true }).replace(UTC_MARK, '');
}

/** The time away: the span from the departure to the answer. */
export function awaySpan(r: Range): string {
  return dur(r.now - r.away);
}

/**
 * The range line under the range row: what the range covers, the time
 * away for SINCE YOU LEFT, and the events. A range of hour rows names its
 * first and its last time in the reader's zone; a range of day rows names
 * UTC days, because a day row is a UTC day. SINCE YOU LEFT past 48 hours
 * names the UTC day of the departure (F4).
 */
export function rangeLine(r: Range): string {
  const parts: string[] = [];
  if (r.unit === 'hour') {
    const from = r.id === 'away' ? r.away : r.first * SEC_PER_HOUR;
    parts.push(S.span({ from: isoNear(from, r.now), to: isoTime(r.now) }));
  } else if (r.id === 'away') {
    parts.push(S.since_day({ day: isoDate(r.first * SEC_PER_DAY, { utc: true }) }));
  } else {
    parts.push(S.span({ from: utcDay(r.first * SEC_PER_DAY), to: isoDate(r.now, { utc: true }) }));
  }
  if (r.id === 'away') parts.push(S.away_for({ span: awaySpan(r) }));
  parts.push(S.events({ n: eventsOf(r.sums) }));
  return parts.join(' · ');
}

/** The faint dash of a zero. */
export const DASH = '<span class="dg-z">–</span>';

/** A figure with its thousands grouped, or a faint dash for zero. */
export function figHtml(v: number): string {
  return v === 0 ? DASH : groupThousands(v);
}

/** A figure in at most four characters, for a slot of a chart grid. */
export function compact(v: number): string {
  if (v < 1000) return String(Math.round(v));
  return S.thousand({ n: Math.round(v / 1000) });
}

/** A change that is not zero, up or down, in at most four characters. */
function upDown(d: number): string {
  return d > 0 ? S.up({ n: compact(d) }) : S.down({ n: compact(-d) });
}

/** The change of a figure against the range before, in the dim ink. */
export function changeText(now: number, before: number): string {
  const d = now - before;
  return d === 0 ? S.same : upDown(d);
}

// ---------------------------------------------------------------------
// The range row

/** The label of a range. */
export function rangeLabel(id: RangeId): string {
  switch (id) {
    case 'away': return S.range_away;
    case 'h24': return S.range_h24;
    case 'd7': return S.range_d7;
    case 'season': return S.range_season;
  }
}

/** The range row: a range picks the rows the block sums and saves
 *  nothing, so it is a tab strip of the choice kit (R2011), the chosen
 *  range open. SINCE YOU LEFT cannot be picked while the ledger holds no
 *  departure. */
export function rangeRowHtml(on: RangeId, hasAway: boolean): string {
  return tabStrip(RANGE_IDS.map((id) => {
    const off = id === 'away' && !hasAway;
    return { id, label: rangeLabel(id), locked: off, title: off ? S.range_no_away : undefined };
  }), on, 'ib-s-range', { label: S.range_away });
}

// ---------------------------------------------------------------------
// The charts: inline SVG in the DOM (ui-map-interface.md §1), the idiom
// of the income graphs of ECONOMY · PRODUCTION (R1489). Every ink is a
// class of `sinceleft.css`, so the sheet holds every colour.

/** The gap between two bars of a slot `slot` wide. */
function barGap(slot: number): number {
  return Math.max(2, Math.min(8, slot * 0.28));
}

function svg(w: number, h: number, body: string, cls: string, stretch = false): string {
  return `<svg class="${cls}" viewBox="0 0 ${w} ${h}"${stretch ? ' preserveAspectRatio="none"' : ''}`
    + ` aria-hidden="true">${body}</svg>`;
}

/** The spark of a figure cell: one line over the rows of the range, with
 *  a dot at its end. A range of no count draws a flat line. The spark
 *  stretches to the room its cell leaves, and the sheet keeps the width
 *  of its line (`vector-effect`). */
export function sparkSvg(values: readonly number[], cls: string): string {
  const w = DIGEST.sparkW, h = DIGEST.sparkH;
  const max = Math.max(0, ...values);
  const n = values.length;
  const x = (i: number) => (n < 2 ? w / 2 : 1 + (i * (w - 2)) / (n - 1));
  const y = (v: number) => (max > 0 ? h - 1.5 - (v / max) * (h - 3) : h - 1.5);
  const pts = values.map((v, i) => `${f1(x(i))},${f1(y(v))}`).join(' ');
  const last = values.length - 1;
  const dot = last >= 0 ? `<circle cx="${f1(x(last))}" cy="${f1(y(values[last]!))}" r="1.6"/>` : '';
  return svg(w, h, `<polyline points="${pts}"/>${dot}`, `sl-spark ${cls}${max > 0 ? '' : ' flat'}`, true);
}

/** Bars up and bars down on ONE scale over one base line (card 20: one
 *  loss is as tall as one sinking). `upInk` is the class of the bars up. */
export function divBarsSvg(up: readonly number[], down: readonly number[], upInk = 'dg-up'): string {
  const w = DIGEST.chartW, h = DIGEST.chartH, n = Math.max(1, up.length);
  const mu = Math.max(0, ...up), md = Math.max(0, ...down);
  const sum = mu + md;
  const base = sum === 0 ? Math.round(h / 2)
    : mu === 0 ? 3 : Math.round(h * Math.max(0.3, Math.min(0.72, mu / sum)));
  const unit = sum === 0 ? 0 : (h - 4) / sum;
  const slot = w / n, gap = barGap(slot);
  let out = '';
  for (let i = 0; i < n; i++) {
    const x = f1(i * slot + gap / 2), bw = f1(slot - gap);
    const hu = (up[i] ?? 0) * unit, hd = (down[i] ?? 0) * unit;
    if (hu > 0) out += `<rect class="${upInk}" x="${x}" y="${f1(base - hu)}" width="${bw}" height="${f1(hu)}"/>`;
    if (hd > 0) out += `<rect class="dg-dn" x="${x}" y="${base + 1}" width="${bw}" height="${f1(hd)}"/>`;
  }
  out += `<path class="dg-base" d="M0 ${base + 0.5}H${w}"/>`;
  return svg(w, h, out, 'dg-ch');
}

/** Bars of one figure from the foot, and a Data Core mark ◆ over each
 *  slot that brought one (card 20: the dives). */
function diveSvg(iso: readonly number[], cores: readonly number[]): string {
  const w = DIGEST.chartW, h = DIGEST.chartH, n = Math.max(1, iso.length);
  const band = 10;
  const max = Math.max(0, ...iso);
  const slot = w / n, gap = barGap(slot);
  let out = '';
  for (let i = 0; i < n; i++) {
    const v = iso[i] ?? 0;
    const bh = max > 0 ? (v / max) * (h - band - 2) : 0;
    if (bh > 0) {
      out += `<rect class="dg-iso" x="${f1(i * slot + gap / 2)}" y="${f1(h - 1 - bh)}" width="${f1(slot - gap)}" height="${f1(bh)}"/>`;
    }
    if ((cores[i] ?? 0) > 0) {
      const cx = i * slot + slot / 2;
      out += `<path class="dg-core" d="M${f1(cx)} 1L${f1(cx + 4)} 5L${f1(cx)} 9L${f1(cx - 4)} 5Z"/>`;
    }
  }
  out += `<path class="dg-base" d="M0 ${h - 0.5}H${w}"/>`;
  return svg(w, h, out, 'dg-ch');
}

/** Three lanes of marks, one for each object result: ◆ taken, ■ held and
 *  ✕ lost, a mark in each slot that holds the result. The ledger counts
 *  the results and names no object (inbox.md §7). */
function objectsSvg(taken: readonly number[], held: readonly number[], lost: readonly number[]): string {
  const w = DIGEST.chartW, h = 34, n = Math.max(1, taken.length);
  const slot = w / n;
  let out = '';
  for (let lane = 0; lane < 3; lane++) out += `<path class="dg-lane" d="M0 ${6 + lane * 11}.5H${w}"/>`;
  for (let i = 0; i < n; i++) {
    const cx = i * slot + slot / 2;
    if ((taken[i] ?? 0) > 0) out += `<path class="dg-good" d="M${f1(cx)} 2L${f1(cx + 4)} 6L${f1(cx)} 10L${f1(cx - 4)} 6Z"/>`;
    if ((held[i] ?? 0) > 0) out += `<rect class="dg-held" x="${f1(cx - 3.5)}" y="13.5" width="7" height="7"/>`;
    if ((lost[i] ?? 0) > 0) {
      out += `<path class="dg-x" d="M${f1(cx - 3.5)} 24.5L${f1(cx + 3.5)} 31.5M${f1(cx + 3.5)} 24.5L${f1(cx - 3.5)} 31.5"/>`;
    }
  }
  return svg(w, h, out, 'dg-ch dg-tl');
}

/** One line of a figure grid: the key mark, the label, a figure for each
 *  slot and the total. */
interface GridRow {
  label: string;
  ink: string;
  values: readonly number[];
  total: number;
}

/** The label of a slot: the hour in the reader's zone for a chart of
 *  hours, the day of the month (UTC) for a chart of days. */
function slotLabel(r: Range, s: Slot): string {
  return r.unit === 'hour' ? isoTime(s.start).slice(0, 2) : utcDay(s.start).slice(8, 10);
}

/** A chart block: the head, the chart over a figure grid whose columns
 *  are the slots of the chart (card 20). */
function chartBlock(r: Range, slots: readonly Slot[], head: string, aside: string, chart: string, rows: readonly GridRow[]): string {
  const step = slotStep(r);
  const per = r.unit === 'hour' ? S.slot_hours({ n: step }) : S.slot_days({ n: step });
  const cells = (vals: readonly string[], cls = 'dg-gv') => `<span class="${cls}">${vals.map((v) => `<i>${v}</i>`).join('')}</span>`;
  return `<section class="dg-cb"><h4 class="dg-sh"><b>${esc(head)}</b><i>${esc(aside)} · ${esc(per)}</i></h4>`
    + `<div class="dg-g" style="--n:${slots.length}">`
    + `<span></span><span class="dg-gc">${chart}</span><span></span>`
    + `<span></span>${cells(slots.map((s) => esc(slotLabel(r, s))), 'dg-gv dg-gx')}<span class="dg-gt dg-gx">${esc(S.g_total)}</span>`
    + rows.map((row) => `<span class="dg-gl"><i class="dg-k ${row.ink}"></i>${esc(row.label)}</span>`
      + cells(row.values.map((v) => (v === 0 ? DASH : esc(compact(v)))))
      + `<span class="dg-gt">${figHtml(row.total)}</span>`).join('')
    + '</div></section>';
}

/** The figures of one counter, slot by slot. */
function column(slots: readonly Slot[], k: InboxLedgerKey): number[] {
  return slots.map((s) => fig(s.sums, k));
}

/** The four charts of card 20 a: the war, the dives, the ground and the
 *  strategic objects, each on the slots of the range. */
export function chartsHtml(r: Range): string {
  const sl = slotsOf(r);
  const sunk = column(sl, 'kills'), lost = column(sl, 'fleets_lost');
  const iso = column(sl, 'isotopes'), cores = column(sl, 'cores'), dives = column(sl, 'dives');
  const won = column(sl, 'cells_won'), cl = column(sl, 'cells_lost');
  const taken = column(sl, 'obj_taken'), held = column(sl, 'obj_held'), ol = column(sl, 'obj_lost');
  const t = (k: InboxLedgerKey) => fig(r.sums, k);
  return chartBlock(r, sl, S.ch_war, S.ch_war_aside, divBarsSvg(sunk, lost), [
    { label: S.g_sunk, ink: 'dg-up', values: sunk, total: t('kills') },
    { label: S.g_lost, ink: 'dg-dn', values: lost, total: t('fleets_lost') },
  ])
    + chartBlock(r, sl, S.ch_dives, S.ch_dives_aside, diveSvg(iso, cores), [
      { label: S.g_iso, ink: 'dg-iso', values: iso, total: t('isotopes') },
      { label: S.g_cores, ink: 'dg-core', values: cores, total: t('cores') },
      { label: S.g_dives, ink: 'dg-faint', values: dives, total: t('dives') },
    ])
    + chartBlock(r, sl, S.ch_ground, S.ch_ground_aside, divBarsSvg(won, cl, 'dg-good'), [
      { label: S.g_won, ink: 'dg-good', values: won, total: t('cells_won') },
      { label: S.g_lost, ink: 'dg-dn', values: cl, total: t('cells_lost') },
    ])
    + chartBlock(r, sl, S.ch_objects, S.ch_objects_aside, objectsSvg(taken, held, ol), [
      { label: S.g_taken, ink: 'dg-good', values: taken, total: t('obj_taken') },
      { label: S.g_held, ink: 'dg-held', values: held, total: t('obj_held') },
      { label: S.g_lost, ink: 'dg-dn', values: ol, total: t('obj_lost') },
    ]);
}

// ---------------------------------------------------------------------
// The top moments (card 20): the largest window battle, the worst loss,
// the best dive and the best sale of the range. The ledger keeps no
// moment record, so the page picks them from the messages that the INBOX
// still holds (inbox.md §7).

export type MomentKey = 'battle' | 'loss' | 'dive' | 'sale';

export interface Moment {
  key: MomentKey;
  msg: InboxMsg;
}

function param(m: InboxMsg, k: string): number {
  return toInt(m.params[k] ?? '');
}

/** The kind of moment a message can be, or null. */
function momentKey(m: InboxMsg): MomentKey | null {
  if (m.event === 'battle_report') return 'battle';
  if (m.event === 'fleet_lost' || m.event === 'fleet_lost_cargo') return 'loss';
  if (m.event.startsWith('expedition_')) return 'dive';
  if (m.event.startsWith('market_sold_')) return 'sale';
  return null;
}

/** How much a message weighs as its moment. The worst loss is the one
 *  that cost a captain, then the most damage: a captain is the one power
 *  that crosses seasons (card 20). The best dive is the most isotopes,
 *  with a Data Core as the tie-break. */
function weight(key: MomentKey, m: InboxMsg): number {
  switch (key) {
    case 'battle': return param(m, 'dealt') + param(m, 'taken');
    case 'loss': {
      const fate = m.params.fate ?? '';
      return (fate === 'captured' ? 2e12 : fate === 'wounded' ? 1e12 : 0) + param(m, 'damage');
    }
    case 'dive': return param(m, 'isotopes') * 2 + (m.params.core === '1' ? 1 : 0);
    case 'sale': return param(m, 'price');
  }
}

/** The moments of the messages filed at or after `from`, in the order of
 *  card 20; a tie goes to the newer message. */
export function momentsOf(inbox: readonly InboxMsg[], from: number): Moment[] {
  const best = new Map<MomentKey, { m: InboxMsg; w: number }>();
  for (const m of inbox) {
    if (Number(m.unix) < from) continue;
    const key = momentKey(m);
    if (key === null) continue;
    const w = weight(key, m);
    const had = best.get(key);
    if (!had || w > had.w || (w === had.w && Number(m.unix) > Number(had.m.unix))) best.set(key, { m, w });
  }
  const order: MomentKey[] = ['battle', 'loss', 'dive', 'sale'];
  return order.flatMap((key) => {
    const b = best.get(key);
    return b ? [{ key, msg: b.m }] : [];
  });
}

/** The head of a moment. */
export function momentHead(key: MomentKey): string {
  switch (key) {
    case 'battle': return S.m_battle;
    case 'loss': return S.m_loss;
    case 'dive': return S.m_dive;
    case 'sale': return S.m_sale;
  }
}

// ---------------------------------------------------------------------
// The ledger table (card 21 a): the figures as rows, grouped by bucket,
// and the ranges as columns, then the change of 7D and the mean of a day.

/** The groups of the table, each with its counters. A vitest holds every
 *  counter of the list to one place here. */
export const LEDGER_GROUPS: readonly [string, readonly InboxLedgerKey[]][] = [
  ['loss', ['fleets_lost', 'captains_captured', 'captains_hurt', 'cargo_lost', 'dmg_taken']],
  ['war', ['kills', 'dmg_dealt', 'torp_hit', 'torp_struck', 'battles', 'salvage']],
  ['hold', ['cells_won', 'cells_lost', 'obj_taken', 'obj_held', 'obj_lost', 'fire_events']],
  ['dive', ['dives', 'isotopes', 'cores']],
  ['trade', ['sales', 'credits']],
  ['works', ['jobs_done', 'research_done', 'ships_ready', 'repairs', 'torps_stowed', 'captains_recruited',
    'practice', 'cargo_landed', 'dockings', 'iso_bought', 'prints_bought', 'cargo_blocked']],
  ['events', ['msg_loss', 'msg_war', 'msg_hold', 'msg_dive', 'msg_trade', 'msg_world']],
];

/** The label of each counter. */
export const FIGURE_LABELS: Readonly<Record<InboxLedgerKey, string>> = {
  msg_loss: S.fig_msg_loss, msg_war: S.fig_msg_war, msg_hold: S.fig_msg_hold,
  msg_dive: S.fig_msg_dive, msg_trade: S.fig_msg_trade, msg_world: S.fig_msg_world,
  fleets_lost: S.fig_fleets_lost, captains_captured: S.fig_captains_captured,
  captains_hurt: S.fig_captains_hurt, cargo_lost: S.fig_cargo_lost, dmg_taken: S.fig_dmg_taken,
  kills: S.fig_kills, dmg_dealt: S.fig_dmg_dealt, torp_hit: S.fig_torp_hit,
  torp_struck: S.fig_torp_struck, battles: S.fig_battles, salvage: S.fig_salvage,
  fire_events: S.fig_fire_events, cells_lost: S.fig_cells_lost, cells_won: S.fig_cells_won,
  obj_taken: S.fig_obj_taken, obj_lost: S.fig_obj_lost, obj_held: S.fig_obj_held,
  dives: S.fig_dives, isotopes: S.fig_isotopes, cores: S.fig_cores,
  sales: S.fig_sales, credits: S.fig_credits,
  jobs_done: S.fig_jobs_done, research_done: S.fig_research_done, ships_ready: S.fig_ships_ready,
  repairs: S.fig_repairs, torps_stowed: S.fig_torps_stowed,
  captains_recruited: S.fig_captains_recruited, practice: S.fig_practice,
  cargo_landed: S.fig_cargo_landed, dockings: S.fig_dockings, iso_bought: S.fig_iso_bought,
  prints_bought: S.fig_prints_bought, cargo_blocked: S.fig_cargo_blocked,
};

/** The title of a label that needs more words than the column holds: the
 *  two damage sums count only the hulls that sank (§5.2). */
const FIGURE_TITLES: Partial<Record<InboxLedgerKey, string>> = {
  dmg_taken: S.fig_dmg_taken_title,
  dmg_dealt: S.fig_dmg_dealt_title,
};

/** The counters whose rise is bad news; a rise of the others is good
 *  news, except the works and the events, whose change is neutral. */
const BAD = new Set<InboxLedgerKey>([
  'fleets_lost', 'captains_captured', 'captains_hurt', 'cargo_lost', 'dmg_taken',
  'torp_struck', 'cells_lost', 'obj_lost', 'fire_events', 'cargo_blocked',
]);
const NEUTRAL = new Set<InboxLedgerKey>([...LEDGER_GROUPS[5]![1], ...LEDGER_GROUPS[6]![1]]);

/** The cell of the change of 7D. */
function deltaCell(k: InboxLedgerKey, now: number, before: number): string {
  const d = now - before;
  if (d === 0) return DASH;
  const ink = NEUTRAL.has(k) ? 'dg-dim' : (d > 0) !== BAD.has(k) ? 'dg-good' : 'dg-bad';
  return `<span class="${ink}">${esc(upDown(d))}</span>`;
}

/** The cell of the mean of a day of the season. */
function dayCell(v: number, days: number): string {
  if (v === 0 || days <= 0) return DASH;
  const mean = v / days;
  return `<span class="dg-dim">${Math.round(mean) === 0 ? esc(S.less_one) : groupThousands(Math.round(mean))}</span>`;
}

/**
 * The ledger table. `ranges` holds each range the answer gives (SINCE YOU
 * LEFT is absent with no departure), `on` lights the column of the chosen
 * range, and `mark` draws the bucket mark of a group in the gutter. On a
 * telephone the gutter and the label stay pinned, and the range columns
 * scroll sideways inside the table.
 */
export function ledgerTableHtml(
  ranges: Partial<Record<RangeId, Range>>, on: RangeId, mark: (group: string) => string,
): string {
  const cols = RANGE_IDS.filter((id) => ranges[id] !== undefined);
  const d7 = ranges.d7, season = ranges.season;
  const days = season ? season.last - season.first + 1 : 0;
  let html = '<div class="dg-lt"><table><thead><tr><th class="dg-tg"></th>'
    + `<th class="dg-tl">${esc(S.t_figure)}</th>`
    + cols.map((id) => `<th class="r${id === on ? ' on' : ''}">${esc(rangeLabel(id))}</th>`).join('')
    + (d7?.prev ? `<th class="r">${esc(S.t_delta)}</th>` : '')
    + (season ? `<th class="r">${esc(S.t_day)}</th>` : '')
    + '</tr></thead><tbody>';
  for (const [group, keys] of LEDGER_GROUPS) {
    keys.forEach((k, i) => {
      const title = FIGURE_TITLES[k];
      html += `<tr${i === 0 ? ' class="dg-g1"' : ''}><td class="dg-tg">${i === 0 ? mark(group) : ''}</td>`
        + `<td class="dg-tl"${title ? ` title="${esc(title)}"` : ''}>${esc(FIGURE_LABELS[k])}</td>`
        + cols.map((id) => `<td class="r${id === on ? ' on' : ''}">${figHtml(fig(ranges[id]!.sums, k))}</td>`).join('')
        + (d7?.prev ? `<td class="r">${deltaCell(k, fig(d7.sums, k), fig(d7.prev, k))}</td>` : '')
        + (season ? `<td class="r">${dayCell(fig(season.sums, k), days)}</td>` : '')
        + '</tr>';
    });
  }
  return html + '</tbody></table></div>';
}
